import type {
  FeatureAccessConfig,
  FeatureAccessKey,
} from '@diamond/shared/types';

import { LIST_FEATURE_FLAGS } from './use-feature-flag';

export const FEATURE_ACCESS_FLAGS = {
  cart_recommendation: LIST_FEATURE_FLAGS.FF_CART_RECOMMENDATION,
  payment_proof_ocr: LIST_FEATURE_FLAGS.FF_PAYMENT_PROOF_OCR,
  homepage_recipe: LIST_FEATURE_FLAGS.FF_HOMEPAGE_RECIPE,
  po_details_data: LIST_FEATURE_FLAGS.FF_PO_DETAILS_DATA,
} satisfies Record<FeatureAccessKey, string>;

export interface NormalizedFeatureAccess {
  featureToSegments: Record<FeatureAccessKey, Set<string>>;
  selectedSegments: Set<string>;
}

export interface HasFeatureArgs {
  featureKey: FeatureAccessKey;
  flagEnabled: boolean;
  flagLoading: boolean;
  normalized: NormalizedFeatureAccess | null;
}

const createFeatureToSegments = (): Record<FeatureAccessKey, Set<string>> => ({
  cart_recommendation: new Set<string>(),
  payment_proof_ocr: new Set<string>(),
  homepage_recipe: new Set<string>(),
  po_details_data: new Set<string>(),
});

export const normalizeFeatureAccess = (
  config: FeatureAccessConfig,
  selectedSegments: string[]
): NormalizedFeatureAccess => {
  const featureToSegments = createFeatureToSegments();

  for (const [segmentKey, segmentConfig] of Object.entries(
    config.feature_segments
  )) {
    for (const featureKey of segmentConfig.features) {
      featureToSegments[featureKey].add(segmentKey);
    }
  }

  return {
    featureToSegments,
    selectedSegments: new Set(selectedSegments),
  };
};

export const hasFeature = ({
  featureKey,
  flagEnabled,
  flagLoading,
  normalized,
}: HasFeatureArgs): boolean => {
  if (flagLoading || !flagEnabled || !normalized) return false;

  const restrictingSegments = normalized.featureToSegments[featureKey];
  if (restrictingSegments.size === 0) return true;

  for (const segment of restrictingSegments) {
    if (normalized.selectedSegments.has(segment)) return true;
  }

  return false;
};
